import React, { useEffect, useContext, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./payment.scss";
import Button from "components/Button/index";
import Success from "components/success/index";
import Input from "components/Input/index";
import { signupContext } from "context/SignupContext";
import { signInContext } from "context/SigninContext";
import { useToast } from "components/Toaster/index";
import useTheme from "hooks/observer";
import { StoragePlan, ManageUser, Organization } from "@induz/sdk";

// Indian GSTIN format: 22AAAAA0000A1Z5
// 2-digit state code (01-37), 5 letters, 4 digits, 1 letter, 1 alphanumeric, Z, 1 alphanumeric
const GST_REGEX = /^[0-3][0-9][A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const isValidGST = (value) => {
  if (!value) return true; // empty is OK (optional field)
  return GST_REGEX.test(value.trim().toUpperCase());
};

// Coupon codes: alphanumeric + hyphens only, max 60 characters
const COUPON_ALLOWED_REGEX = /[^a-zA-Z0-9-]/g;

function Payment({
  setSuccess,
  success,
  setChange,
  selectPrice,
  nextStep
}) {
  const status = true;
  const {
    setOrderId,
    setAmount,
    setCurrency,
    setPaymentObj,
    setPaymentKey,
    selectedPlanData,
    isTypeSelect,
    orgnization: signupOrg,
    user: signupUser,
    setUser: setSignupUser,
    setOrgnization: setSignupOrg,
    couponEmail
  } = useContext(signupContext);

  // Also check signInContext as fallback
  const { user: signInUser, orgnization: signInOrg, isTypeSelect: signInTypeSelect } = useContext(signInContext);

  const showToast = useToast();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0); // Discount from backend
  const lastValidatedRef = useRef("");
  const requestIdRef = useRef(0);
  const [gstToken, setGstToken] = useState("");
  const [gstError, setGstError] = useState("");
  const [paymentSkippedBecauseZero, setPaymentSkippedBecauseZero] = useState(false);

  // Use user/org from signupContext, fallback to signInContext
  const user = signupUser || signInUser;
  const orgnization = signupOrg || signInOrg;
  const effectiveIsTypeSelect = isTypeSelect || signInTypeSelect || "individual";

  const planData = selectedPlanData || (typeof selectPrice === "object" && selectPrice !== null ? selectPrice : null);
  const planName = planData?.name ?? "";
  const planPrice = planData?.price ?? (typeof selectPrice === "number" ? selectPrice : (typeof selectPrice === "string" ? parseFloat(selectPrice) || 0 : 0));

  // Apply coupon to plan price first, then GST on the reduced amount.
  // Use planData.taxAmount or planData.taxRate when backend sends them; otherwise 18% on amountAfterCoupon.
  const planPriceNum = typeof planPrice === "number" ? planPrice : 0;
  const amountAfterCoupon = Math.max(0, planPriceNum - discountAmount);
  const taxRate = planData?.taxRate != null ? Number(planData.taxRate) : (planData?.taxAmount == null ? 18 : null);
  const taxAmountFromBackend = planData?.taxAmount != null ? Number(planData.taxAmount) : null;
  const taxAmount = taxAmountFromBackend != null
    ? taxAmountFromBackend
    : (taxRate != null && !Number.isNaN(taxRate) ? Math.round((amountAfterCoupon * taxRate) / 100) : 0);
  const totalAmount = amountAfterCoupon + taxAmount;

  const handlePayNow = async () => {
    if (isLoading) return;

    // Validate GST token if provided
    const gstTrimmed = (gstToken || "").trim().toUpperCase();
    if (gstTrimmed && !isValidGST(gstTrimmed)) {
      setGstError("Invalid GSTIN format (e.g. 22AAAAA0000A1Z5)");
      showToast("Please enter a valid GST number", "error");
      return;
    }

    // Track the effective discount & coupon for this call.
    // We can't rely on React state (discountAmount / appliedCoupon) because
    // setState is async and won't be reflected within the same execution.
    let effectiveDiscount = discountAmount;
    let effectiveCoupon = appliedCoupon;

    // If user typed a coupon code but hasn't applied it yet, validate it first
    const couponTrimmed = (couponCode || "").trim();
    if (couponTrimmed && !appliedCoupon) {
      try {
        const result = await validateCouponCode(couponCode, { immediate: true });
        if (result) {
          effectiveDiscount = result.discount;
          effectiveCoupon = result.coupon;
        }
      } catch {
        // Coupon validation failed — toast already shown by validateCouponCode.
        // Stop here; don't proceed with payment.
        return;
      }
    }

    // Recalculate amounts with the freshest discount value
    const freshAmountAfterCoupon = Math.max(0, planPriceNum - effectiveDiscount);
    const freshTaxAmount = taxAmountFromBackend != null
      ? taxAmountFromBackend
      : (taxRate != null && !Number.isNaN(taxRate) ? Math.round((freshAmountAfterCoupon * taxRate) / 100) : 0);
    const freshTotalAmount = freshAmountAfterCoupon + freshTaxAmount;
    const noPaymentRequired = freshAmountAfterCoupon <= 0 || freshTotalAmount <= 0;

    setIsLoading(true);
    try {
      // Get planId from selectedPlanData (from backend)
      const planId = selectedPlanData?.planId;
      const isAnnual = selectedPlanData?.billingPeriod === "annual";
      if (planId < 0) {
        throw new Error("Plan ID not found. Please select a plan.");
      }

      // Fetch user/org if not available in context
      let currentUser = user;
      let currentOrg = orgnization;
      let currentIsTypeSelect = effectiveIsTypeSelect;

      if (!currentUser && !currentOrg) {
        console.log("🔍 User/Org not in context, fetching from backend...");
        const userMail = sessionStorage.getItem("mail");
        if (!userMail) {
          throw new Error("User email not found. Please log in again.");
        }

        const fetchedUser = await ManageUser.getUserDetails(userMail);
        if (fetchedUser instanceof Organization) {
          currentOrg = fetchedUser;
          currentIsTypeSelect = "org";
          setSignupOrg(fetchedUser);
        } else {
          currentUser = fetchedUser;
          currentIsTypeSelect = "individual";
          setSignupUser(fetchedUser);
        }
        console.log("✅ Fetched user/org from backend:", fetchedUser instanceof Organization ? "Organization" : "User");
      }

      // Always call startPayment (including when total is 0) so backend can apply plan/coupon
      let payment;
      console.log("Initiating payment with:", { planId, isAnnual, effectiveCoupon, gstToken });
      console.log("coupon", effectiveCoupon, couponCode);
      if (currentIsTypeSelect !== "individual" && currentOrg) {
        payment = await currentOrg.startPayment(planId, isAnnual, undefined, effectiveCoupon || undefined, gstToken?.trim() || undefined);
      } else if (currentIsTypeSelect === "individual" && currentUser) {
        payment = await currentUser.startPayment(planId, isAnnual, undefined, effectiveCoupon || undefined, gstToken?.trim() || undefined);
      } else {
        throw new Error("Organization or User object not available. Please try again.");
      }

      // When total is 0: backend succeeded — show "Account created successfully" page (do not open Razorpay)
      if (noPaymentRequired) {
        console.log("✅ Start payment (zero amount) succeeded. Showing account created success.");
        setPaymentSkippedBecauseZero(true);
        setSuccess(true);
        return;
      }

      // Non-zero: set payment in context and go to Razorpay step
      setPaymentObj(payment);
      setOrderId(payment.orderId);
      setPaymentKey(payment.razorpayPaymentId);
      setAmount(payment.amount);
      setCurrency("INR");

      console.log("Payment initiated:", {
        orderId: payment.orderId,
        razorpayKey: payment.razorpayPaymentId,
        amount: payment.amount
      });

      nextStep();
    } catch (error) {
      console.error("Failed to initiate payment:", error);
      showToast(error.message || "Failed to initiate payment. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    // Immediate (manual) validation triggered by Apply button
    await validateCouponCode(couponCode, { immediate: true });
  };

  // Core validation function used by Apply button and handlePayNow pre-check
  const validateCouponCode = async (code, { immediate = false } = {}) => {
    const couponTrimmed = (code || "").trim();
    if (!couponTrimmed) {
      if (immediate) showToast("Please enter a coupon code", "warning");
      return;
    }

    const couponNormalized = couponTrimmed;
    if (lastValidatedRef.current === couponNormalized) return;

    const myId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const billingPeriod = planData?.billingPeriod || "monthly";
      const isAnnual = billingPeriod === "annual";
      const mailForCoupon = couponEmail || (user && typeof user.getEmail === "function" ? user.getEmail() : "") || (orgnization && typeof orgnization.getEmail === "function" ? orgnization.getEmail() : "");

      let discount;
      if (effectiveIsTypeSelect !== "individual" && orgnization && typeof orgnization.validateCoupon === "function") {
        discount = await orgnization.validateCoupon(couponNormalized, isAnnual);
      } else {
        const org = new Organization();
        org.setEmail(mailForCoupon);
        discount = await org.validateCoupon(couponNormalized, isAnnual);
      }

      if (requestIdRef.current !== myId) return; // stale result

      const finalDiscount = discount || 0;
      setAppliedCoupon(couponNormalized);
      setDiscountAmount(finalDiscount);
      lastValidatedRef.current = couponNormalized;
      showToast("Coupon applied successfully!", "success");
      return { coupon: couponNormalized, discount: finalDiscount };
    } catch (error) {
      if (requestIdRef.current !== myId) return; // stale
      console.error("❌ Failed to validate coupon:", error);
      setAppliedCoupon(null);
      setDiscountAmount(0);
      showToast(error.message || "Invalid coupon code. Please try again.", "error");
      throw error; // Re-throw so handlePayNow can catch it and stop payment
    } finally {
      if (requestIdRef.current === myId) setIsLoading(false);
    }
  };


  const handleRemoveCoupon = () => {
    const code = appliedCoupon;
    setAppliedCoupon(null);
    setCouponCode("");
    setDiscountAmount(0);
    lastValidatedRef.current = "";
    showToast("Coupon removed successfully " , "success");
  };



  return (
    <>
      {!success ? (
        <div className="Payment">
          <div className="PaymentTitle">
            <h1>Payment </h1>
            <p>
              Please complete the payment to unlock full access to your account.
            </p>
          </div>
          <div className="PaymentWrapper">
            <div className="PaymentSummary">
              <p>Summary</p>
              <div className="PaymentPlan">
                <div className="tax">
                  <p>{planName || "Plan"}</p>
                  <strong>₹{typeof planPrice === "number" ? new Intl.NumberFormat("en-IN").format(planPrice) : planPrice}</strong>
                </div>
                <div className="tax">
                  <p>Tax/GST</p>
                  <strong>₹{taxAmount}</strong>
                </div>
                {appliedCoupon && discountAmount > 0 && (
                  <div className="tax coupon">
                    <p>Coupon ({appliedCoupon})</p>
                    <strong>-₹{new Intl.NumberFormat("en-IN").format(discountAmount)}</strong>
                  </div>
                )}
                <span></span>
                <div className="tax">
                  <p className="total">Total</p>
                  <strong className="total">₹{new Intl.NumberFormat("en-IN").format(totalAmount)}</strong>
                </div>
              </div>
            </div>
            <div className="PaymentCoupon">
              <p>Have a coupon code or invoice?</p>
              <div className="PaymentCouponInput">
                <Input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(COUPON_ALLOWED_REGEX, "").slice(0, 60);
                    setCouponCode(sanitized);
                  }}
                  bg={theme === "light" ? "#fff" : "transparent"}
                  width="100%"
                  maxLength={60}
                  disabled={!!appliedCoupon}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApplyCoupon();
                    }
                  }}
                />
                {appliedCoupon ? (
                  <Button
                    type="white"
                    title="Remove"
                    onClick={handleRemoveCoupon}
                    width="fit-content"
                    padding="14px 18px"
                  />
                ) : (
                  <Button
                    type="violet"
                    title="Apply"
                    onClick={handleApplyCoupon}
                    width="fit-content"
                    padding="14px 18px"
                  />
                )}
              </div>
            </div>
            <div className="PaymentCoupon">
              <p>GST token</p>
              <div className="PaymentCouponInput">
                <Input
                  type="text"
                  placeholder="Enter GST number (e.g. 22AAAAA0000A1Z5)"
                  value={gstToken}
                  onChange={(e) => {
                    const upper = e.target.value.toUpperCase().slice(0, 15);
                    setGstToken(upper);
                    if (upper && !isValidGST(upper)) {
                      setGstError("Invalid GSTIN format (e.g. 22AAAAA0000A1Z5)");
                    } else {
                      setGstError("");
                    }
                  }}
                  bg={theme === "light" ? "#fff" : "transparent"}
                  width="100%"
                  maxLength={15}
                  errorMessage={gstError}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handlePayNow();
                    }
                  }}
                />
              </div>
            </div>
            <Button
              type="violet"
              title={isLoading ? "Processing..." : "Pay Now"}
              onClick={handlePayNow}
            />
          </div>
        </div>
      ) : (
        <Success
          title={paymentSkippedBecauseZero || !status ? "Account Created Successfully" : "Payment Failed!"}
          subTitle={
            paymentSkippedBecauseZero || !status
              ? "Your account has been successfully signed up. Enjoy exploring our services!"
              : "Your payment has failed. Please try again to complete the transaction."
          }
          buttonText={paymentSkippedBecauseZero ? "Sign in" : (!status ? "Add users" : "Retry Payment")}
          buttonPlus={!status && !paymentSkippedBecauseZero}
          onClick={() => {
            if (paymentSkippedBecauseZero) {
              navigate("/signin", { replace: true });
              window.location.reload();
            } else if (status) {
              setChange("payment");
              setSuccess("");
            } else {
              nextStep();
            }
          }}
          success={paymentSkippedBecauseZero || !status}
          background={true}
        />
      )}
    </>
  );
}

export default Payment;
